// Turns the AI interview's per-section verbatim transcripts (S.ai.t) into
// the wizard's structured fields, via POST /api/transcription/structured
// (one call per section, in parallel). Pure functions, no Vue coupling —
// mirrors claimStepMapper.js's style, just in the reverse direction (into S
// rather than out of it).
//
// 'driver' has no home in S today (see NM2-416 plan) — deliberately absent
// from EXTRACTION_SCHEMAS/APPLY_FIELDS below; its transcript stays
// verbatim-only.
import {
  EXTRACTION_SCHEMAS,
  DEFAULT_EXTRACTION_PROMPT,
} from '@/data/aiExtractionSchemas'
import { extractStructured } from '@/services/claim-service'

// Only take an extracted value when the model actually found one — schema
// fields it couldn't state come back null, and must not overwrite whatever
// is already in S (typed in separately, or left over from an earlier run).
function pick(value, fallback = '') {
  return value === null || value === undefined || value === ''
    ? fallback
    : value
}

export function applyStoryFields(S, data) {
  if (!data) return {}
  return {
    incidentLocation: {
      ...S.incidentLocation,
      locationName: pick(data.locationName, S.incidentLocation.locationName),
    },
    surface: pick(data.surface, S.surface),
    weather: pick(data.weather, S.weather),
    vis: pick(data.vis, S.vis),
    light: pick(data.light, S.light),
  }
}

export function applyPeopleFields(S, data) {
  const people = Array.isArray(data?.people) ? data.people : []
  if (people.length === 0) return {}
  return {
    people: people.map((p) => ({
      name: pick(p.name),
      age: pick(p.age),
      role: pick(p.role),
      injured: pick(p.injured),
      hospital: pick(p.hospital),
    })),
  }
}

export function applyOtherFields(S, data) {
  const vehicles = Array.isArray(data?.vehicles) ? data.vehicles : []
  const changes = {}
  if (vehicles.length > 0) {
    changes.vehicles = vehicles.map((v) => ({
      reg: pick(v.reg),
      makeModel: pick(v.makeModel),
      owner: pick(v.owner),
      phone: pick(v.phone),
      insurer: pick(v.insurer),
      damage: pick(v.damage),
    }))
  }
  const otherProperty = data && pick(data.otherProperty, '')
  if (otherProperty) changes.otherProperty = otherProperty
  return changes
}

export function applyWitnessFields(S, data) {
  if (!data) return {}
  return {
    witnessName: pick(data.witnessName, S.witnessName),
    witnessPhone: pick(data.witnessPhone, S.witnessPhone),
    police:
      data.police === null || data.police === undefined
        ? S.police
        : data.police,
    station: pick(data.station, S.station),
    ob: pick(data.ob, S.ob),
  }
}

export function applyDamageFields(S, data) {
  if (!data) return {}
  const changes = {
    severity: pick(data.severity, S.severity),
    preex: pick(data.preex, S.preex),
  }
  if (Array.isArray(data.areas) && data.areas.length > 0) {
    changes.areas = data.areas
  }
  return changes
}

const APPLY_FIELDS = {
  story: applyStoryFields,
  people: applyPeopleFields,
  other: applyOtherFields,
  witness: applyWitnessFields,
  damage: applyDamageFields,
}

// story/damage are free-text sections already — "describe the accident" and
// "describe the damage" — so their transcript IS the field value, copied
// as-is rather than run through extraction.
function applyVerbatimFields(S, sections) {
  const changes = {}
  if (
    sections.includes('story') &&
    typeof S.ai.t.story === 'string' &&
    S.ai.t.story.trim()
  )
    changes.story = S.ai.t.story
  if (
    sections.includes('damage') &&
    typeof S.ai.t.damage === 'string' &&
    S.ai.t.damage.trim()
  )
    changes.damageDesc = S.ai.t.damage
  return changes
}

// Fires one extraction call per captured section (skips sections never
// answered — 'people'/'other' are optional) in parallel via allSettled, so
// one section rejecting — or the model leaving every field in it
// unmentioned — never blocks the others or the caller. Returns a single
// merged changes object ready to spread onto S; callers that want section
// isolation on failure get it for free since a rejected/unsuccessful
// section just contributes nothing.
export async function extractInterviewFields(S) {
  // Skip sections already extracted (S.ai.ex) so a re-run can't undo hand edits.
  const extracted = S.ai.ex || {}
  const sections = Object.keys(APPLY_FIELDS).filter((key) => {
    const transcript = S.ai.t[key]
    if (typeof transcript !== 'string' || transcript.trim().length === 0)
      return false
    return transcript !== extracted[key]
  })

  const results = await Promise.allSettled(
    sections.map((key) =>
      extractStructured(
        S.ai.t[key],
        EXTRACTION_SCHEMAS[key],
        DEFAULT_EXTRACTION_PROMPT
      )
    )
  )

  let changes = applyVerbatimFields(S, sections)
  results.forEach((result, i) => {
    if (result.status !== 'fulfilled') return
    const envelope = result.value.data
    if (!envelope?.success) return
    const extracted = envelope.data?.data
    if (!extracted) return
    changes = { ...changes, ...APPLY_FIELDS[sections[i]](S, extracted) }
  })
  return changes
}
